import React from "react";
import styled from "styled-components";
import {
  Grid,
  Typography,
  Box,
  Stack,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectSelectedSchool } from "app/modules/schools/selectors";
import dayjs from "dayjs";
import { IndianCurrency, NumberInWord } from "app/utils/helper";

const InvoiceTemplate1 = ({
  invoiceText,
  data,
  invoiceItems,
  calculateGrandTotal,
  calculateTotalDiscount,
  calculateTotalFine,
  calculateTotalBalance,
  keepHeaderSpace = false,
}) => {
  const selectedSchool = useSelector(selectSelectedSchool);
  const isMobile = useMediaQuery("(max-width:768px)");

  return (
    <LeftSideDiv isMobile={isMobile}>
      <InvoiceHeader>
        {keepHeaderSpace ? (
          <Box sx={{ height: "180px" }} />
        ) : (
          <div
            style={{
              width: "100%",
            }}
          >
            <Grid container sx={{ bgcolor: "#f8f9fb", py: 0.5 }}>
              <Grid
                item
                xs={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selectedSchool?.schoolLogo && (
                  <Box
                    sx={{
                      width: 55,
                      height: 55,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #d0d0e2",
                      borderRadius: "6px",
                      bgcolor: "#fff",
                      p: 0.25,
                    }}
                  >
                    <img
                      src={selectedSchool?.schoolLogo}
                      style={{
                        maxHeight: "100%",
                        maxWidth: "100%",
                        objectFit: "contain",
                        borderRadius: "4px",
                      }}
                      alt="school-logo"
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={10} sx={{ pl: 1 }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#0C4B7E",
                    letterSpacing: "0.25px",
                  }}
                >
                  {selectedSchool?.schoolName}
                </Typography>

                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "10px",
                    fontWeight: 500,
                    color: "#333",
                    mt: 0.25,
                  }}
                >
                  {`${selectedSchool?.schoolAddress?.address}, ${selectedSchool?.schoolAddress?.dist}, ${selectedSchool?.schoolAddress?.state}, ${selectedSchool?.schoolAddress?.pinCode}`}
                </Typography>

                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "9px",
                    fontWeight: 500,
                    color: "#666",
                    mt: 0.25,
                  }}
                >
                  {selectedSchool?.schoolContactNumber}, {selectedSchool?.email}
                </Typography>
              </Grid>
            </Grid>
            <Box
              sx={{
                width: "100%",
                fontWeight: 700,
                fontSize: "12px",
                letterSpacing: "1px",
                textTransform: "uppercase",
                textAlign: "center",
                color: "#fff",
                bgcolor: "#1e4976",
                py: 0.5,
                borderBottom: "3px solid #FFA726",
              }}
            >
              {invoiceText} - {`${data?.session}`}
            </Box>
          </div>
        )}
      </InvoiceHeader>
      <InvoiceBody>
        <Grid
          container
          sx={{
            px: 1,
            py: 0.75,
            bgcolor: "#fafbfc",
            borderBottom: "1px solid #d0d0e2",
          }}
        >
          <Grid item xs={4} sx={{ borderRight: "1px solid #e8eaed", pr: 1 }}>
            <Typography
              sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
            >
              Invoice No.
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
              {data?.invoiceNumber}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ borderRight: "1px solid #e8eaed", px: 1 }}>
            <Typography
              sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
            >
              Collection Date
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
              {dayjs(data?.collectionDate).format("DD MMM, YYYY")}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ pl: 1 }}>
            <Typography
              sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
            >
              Depositor Name
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
              {data?.depositorName}
            </Typography>
          </Grid>
          {/* student detail */}
          <Box
            sx={{
              borderRadius: "6px",
              border: "1px solid #d0d0e2",
              bgcolor: "#f8f9fb",
              p: 1,
              m: 1,
              display: "flex",
              direction: "row",
              gap: 2,
              width: "calc(100% - 16px)",
            }}
          >
            <Box
              sx={{
                width: 55,
                height: 55,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #d0d0e2",
                borderRadius: "6px",
                bgcolor: "#fff",
                p: 0.25,
              }}
            >
              <img
                src={
                  data?.studentDetail?.profilePic || "/image-placeholder.jpeg"
                }
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                  borderRadius: "4px",
                }}
                alt="student profile"
              />
            </Box>
            <Grid container spacing={0.5}>
              <Grid item xs={3} md={2}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Name
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
                  {data?.studentDetail?.studentName}
                </Typography>
              </Grid>

              <Grid item xs={3} lg={3}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Father's Name
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
                  {data?.studentDetail?.fatherName}
                </Typography>
              </Grid>
              <Grid item xs={3} lg={3}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Mother's Name
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
                  {data?.studentDetail?.motherName}
                </Typography>
              </Grid>
              <Grid item xs={3} lg={3}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Class-Str-Sec
                </Typography>

                <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
                  {data?.studentDetail?.className_stream_sec}
                </Typography>
              </Grid>
              <Grid item xs={3} lg={2}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Reg No
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
                  {data?.studentDetail?.registrationNumber}
                </Typography>
              </Grid>
              <Grid item xs={3} lg={3}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Adm No
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
                  {data?.studentDetail?.admissionNumber}
                </Typography>
              </Grid>

              <Grid item xs={3} md={3}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Mobile Number
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "10px" }}>
                  {data?.studentDetail?.mobileNumber}
                </Typography>
              </Grid>
              <Grid item xs={3} md={4}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: "9px", color: "#666" }}
                >
                  Transport Info
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "10px",
                    wordWrap: "breakWord",
                    overflowWrap: "breakWord",
                  }}
                >
                  {data?.transportInfo?.vehicleNumber
                    ? `${data?.transportInfo?.vehicleNumber}: ${data?.transportInfo?.to}`
                    : "--"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Box
          sx={{
            bgcolor: "#FFA726",
            py: 0.75,
            px: 1,
            borderTop: "1px solid #e0e0e0",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Grid container>
            <Grid item xs={9}>
              <Typography
                sx={{ fontSize: "11px", fontWeight: 700, color: "#000" }}
              >
                Fee Type
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{ fontSize: "11px", fontWeight: 700, color: "#000" }}
              >
                Paying
              </Typography>
            </Grid>
          </Grid>
        </Box>
        {invoiceItems?.map((invoice, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: i % 2 === 0 ? "#fafbfc" : "#fff",
              borderBottom: "1px solid #e8eaed",
            }}
          >
            <Grid container sx={{ px: 1, py: 0.5 }}>
              <Grid item xs={9}>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  {invoice?.feeType}
                </Typography>
                <Typography
                  sx={{ fontSize: "9px", fontWeight: 400, color: "#666" }}
                >
                  {[...new Set(invoice?.months)]?.join(", ")}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{ fontSize: "10px", fontWeight: 700, color: "#2e7d32" }}
                >
                  {IndianCurrency(invoice?.totalAmount)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Grid
          container
          sx={{
            px: 1,
            py: 0.75,
            bgcolor: "#f0f2f5",
            borderTop: "2px solid #d0d0e2",
            borderBottom: "1px solid #d0d0e2",
          }}
        >
          <Grid item xs={9}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              Grand Total
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              sx={{ fontSize: "11px", fontWeight: 700, color: "#1e4976" }}
            >
              {IndianCurrency(calculateGrandTotal())}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            px: 1,
            py: 0.75,
            bgcolor: "#fafbfc",
            borderBottom: "1px solid #e8eaed",
          }}
        >
          <Grid item xs={4}>
            {calculateTotalDiscount() !== 0 && (
              <>
                <Typography
                  sx={{ fontSize: "9px", fontWeight: 600, color: "#666" }}
                >
                  Total Discount
                </Typography>
                <Typography
                  sx={{ fontSize: "10px", fontWeight: 700, color: "#e53935" }}
                >
                  {IndianCurrency(calculateTotalDiscount())}
                </Typography>
              </>
            )}
          </Grid>
          <Grid item xs={4}>
            <Typography
              sx={{ fontSize: "9px", fontWeight: 600, color: "#666" }}
            >
              Total Fine
            </Typography>
            <Typography
              sx={{ fontSize: "10px", fontWeight: 700, color: "#f57c00" }}
            >
              {IndianCurrency(calculateTotalFine())}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              sx={{ fontSize: "9px", fontWeight: 600, color: "#666" }}
            >
              Total Balance
            </Typography>
            <Typography
              sx={{ fontSize: "10px", fontWeight: 700, color: "#1976d2" }}
            >
              {IndianCurrency(calculateTotalBalance())}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            px: 1,
            py: 0.75,
            bgcolor: "#e8f5e9",
            borderTop: "2px solid #4caf50",
            borderBottom: "2px solid #4caf50",
          }}
        >
          <Grid item xs={9}>
            <Typography
              sx={{ fontSize: "10px", fontWeight: 600, color: "#1b5e20" }}
            >
              Paid Amount:{" "}
              <span style={{ textTransform: "capitalize" }}>
                {NumberInWord(calculateGrandTotal() + calculateTotalFine())}{" "}
                Rupees Only
              </span>
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              sx={{ fontSize: "10px", fontWeight: 700, color: "#1b5e20" }}
            >
              {IndianCurrency(
                Number(calculateGrandTotal()) + Number(calculateTotalFine())
              )}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            px: 1,
            py: 0.75,
            bgcolor: "#fafbfc",
            borderBottom: "1px solid #e8eaed",
          }}
        >
          <Grid item xs={4}>
            <Typography
              sx={{ fontSize: "9px", fontWeight: 600, color: "#666" }}
            >
              Payment Mode
            </Typography>
            <Typography sx={{ fontSize: "10px", fontWeight: 700 }}>
              {data?.paymentDetails
                ? Object.keys(data?.paymentDetails)[0]
                : "--"}
            </Typography>
          </Grid>
          {data?.paymentDetails?.UpiMode && (
            <Grid item xs={8}>
              <Stack direction={"row"} justifyContent={"space-around"}>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  UPI Number: {data?.paymentDetails?.UpiMode?.upiNumber}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Transaction ID: {data?.paymentDetails?.UpiMode?.transactionId}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Amount: {data?.paymentDetails?.UpiMode?.amount}
                </Typography>
              </Stack>
            </Grid>
          )}
          {data?.paymentDetails?.CardMode && (
            <Grid item xs={8}>
              <Stack direction={"row"} justifyContent={"space-around"}>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Card Holder Name:{" "}
                  {data?.paymentDetails?.CardMode?.cardHolderName}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Card Number: {data?.paymentDetails?.CardMode?.cardNumber}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Amount: {data?.paymentDetails?.CardMode?.amount}
                </Typography>
              </Stack>
            </Grid>
          )}
          {data?.paymentDetails?.ChequeMode && (
            <Grid item xs={8}>
              <Stack direction={"row"} justifyContent={"space-around"}>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Cheque No.: {data?.paymentDetails?.ChequeMode?.chequeNumber}
                </Typography>

                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Amount: {data?.paymentDetails?.ChequeMode?.amount}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Deposition Date:{" "}
                  {dayjs(
                    data?.paymentDetails?.ChequeMode?.depositionDate
                  ).format("DD MMM YYYY")}
                </Typography>
              </Stack>
            </Grid>
          )}
          {data?.paymentDetails?.NEFTMode && (
            <Grid item xs={8}>
              <Stack direction={"row"} justifyContent={"space-around"}>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Reference No.:{" "}
                  {data?.paymentDetails?.NEFTMode?.referenceNumber}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Amount: {data?.paymentDetails?.NEFTMode?.amount}
                </Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Transaction No.:{" "}
                  {data?.paymentDetails?.NEFTMode?.transactionNumber}
                </Typography>
              </Stack>
            </Grid>
          )}
          {data?.paymentDetails?.CashMode && (
            <Grid item xs={8}>
              <Stack direction={"row"} justifyContent={"space-around"}>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>
                  Amount: {data?.paymentDetails?.CashMode?.amount}
                </Typography>
              </Stack>
            </Grid>
          )}
        </Grid>
        {data?.status === "DELETED" && (
          <>
            <Divider
              sx={{ background: (theme) => theme.palette.primary.main }}
            />

            <Grid container sx={{ px: 1 }}>
              <Grid item xs={2}>
                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                  Status :{" "}
                  <span
                    style={{
                      color: "red",
                    }}
                  >
                    {data?.status ?? "--"}
                  </span>
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                  At :{" "}
                  <span
                    style={{
                      color: "red",
                    }}
                  >
                    {data?.updatedOn
                      ? dayjs(data?.updatedOn).format("DD MMM YYYY h:mm A")
                      : "--"}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Stack direction={"row"} justifyContent={"space-around"}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                    Deletion Remarks : {data?.deletionRemarks ?? "--"}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            px: 1,
            py: 1,
            bgcolor: "#f8f9fb",
            borderTop: "1px solid #d0d0e2",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "9px", color: "#666" }}>
              Created By : {data?.createdBy?.personName}
            </Typography>
            <Typography sx={{ fontSize: "9px", color: "#666", mt: 0.25 }}>
              Created On : {dayjs(data?.createdOn).format("DD MMM YYYY h:mm A")}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "9px", color: "#666" }}>
              For,
            </Typography>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, mt: 0.25 }}>
              {data?.schoolName}
            </Typography>
            <Typography sx={{ fontSize: "9px", pt: 2, pb: 0.5, color: "#666" }}>
              Authorised Sign.
            </Typography>
          </Box>
        </Stack>
      </InvoiceBody>
    </LeftSideDiv>
  );
};

export default InvoiceTemplate1;

const LeftSideDiv = styled.div`
  margin: 5px;
  width: 100%;
  height: 100%;
  border: 1px solid black;
  background-color: #fff;
  margin-bottom: ${(props) => (props.isMobile ? "100px" : "0")};
`;

const InvoiceHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const InvoiceBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
